import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "./css/HomePageWriteUpOne.css";

const HomePageWriteUpOne = () => {
  return (
    <div className="col-sm-12 col-md-12" style={{ margin: "10px" }}>
      <div className="about-para">
        {/* Problem Overview */}
        <Accordion className="custom-accordion" defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">What Are We Solving?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <p>
              We are solving one of the most ignored yet critical problems in
              India’s education system: <b>career decision-making</b> among
              students from Class 6 to 10, especially in Tier 2 and Tier 3
              regions.
            </p>
            <p>
              As we move away from Tier 1 cities, the gap widens. Many schools
              still struggle to ensure academic independence, leaving students
              dependent on tuition and coaching centers. Exposure to
              opportunities like Olympiads, NTSE, and national-level platforms
              remains limited, sometimes even till Class 12.
            </p>
            <p>
              At the same time, essential life skills such as communication,
              awareness, and self-exploration are rarely nurtured from an early
              stage, even after NEP and CBSE guidelines are there for
              implementing vocational skills from class 6th to 10th. Many
              schools are trying to do it but their system is still unstructured
              and needs to be structured.
            </p>
            <p>
              But the reality is all these factors silently shape the{" "}
              <b>
                first major life decision a student makes: choosing a stream
                after Class 10.
              </b>
            </p>
            <p>
              That’s the gap. That’s the problem. And that’s what we are
              solving.
            </p>
          </AccordionDetails>
        </Accordion>

        {/* Career Decision Problem */}
        <Accordion className="custom-accordion">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              What is the Career Decision Problem?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <p>
              Career decision-making is not a one-time event, it is a{" "}
              <b>lifelong journey of choices.</b>
            </p>
            <p>It begins with :</p>
            <ul>
              <li>Choosing a stream after Class 10</li>
              <li>Selecting a field and specialization after Class 12</li>
            </ul>
            <p>And continues into:</p>
            <ul>
              <li>Higher education decisions</li>
              <li>Career domains, roles, and responsibilities</li>
              <li>Switching industries or paths</li>
              <li>Exploring passive income or passion projects</li>
              <li>Even post-retirement engagements</li>
            </ul>
            <p>
              The real problem is not just <b>“what to choose”</b> but
              <b> how prepared someone is to make informed decisions</b> at
              every stage of life. And choosing is one thing and then following
              the chosen path in the best way is another thing.
            </p>
            <p>
              The most effective way to solve the career decision problem is to
              address it at every stage of life ensuring that all influencing
              factors are consistently considered, understood, and meaningfully
              explored.
            </p>
          </AccordionDetails>
        </Accordion>

        {/* Existing Solutions */}
        <Accordion className="custom-accordion">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              Existing Solutions & Their Gaps & Loopholes
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <p>
              <b>
                1. Career Counselling (Individuals / Organizations / Online
                Platforms)
              </b>
            </p>

            <p>
              <b>Basis:</b>
            </p>
            <p>
              Academics + psychology + Random Skill analysis + Interest + ETC.
            </p>

            <p>
              <b>Loopholes:</b>
            </p>
            <p>
              Psychology at this age is unstable due to frequent hormonal and
              personality changes and it changes often with aging and other
              number of conditions.
            </p>

            <p>No real exposure to:</p>
            <ul>
              <li>Streams.</li>
              <li>
                Professional life outcomes after choosing a particular stream.
              </li>
              <li>
                Skills & extracurriculars not mapped due to lack of vocational
                integration.
              </li>
              <li>One-time or limited interaction.</li>
              <li>No long-term tracking.</li>
            </ul>

            <p>No structured support for:</p>
            <ul>
              <li>Post-12th decisions</li>
              <li>Graduation field selection</li>
              <li>College/university planning</li>
            </ul>

            <p>
              <b>2. Open Career Seminars</b>
            </p>

            <p>
              <b>Loopholes:</b>
            </p>
            <ul>
              <li>One-time exposure → no continuity</li>
              <li>No tracking or follow-up</li>
              <li>
                Limited reach (more in Tier 1, less adaptable in Tier 2/3)
              </li>
            </ul>

            <p>Lacks:</p>
            <ul>
              <li>Personalization</li>
              <li>Real exposure</li>
              <li>Decision framework</li>
              <li>Long-term guidance</li>
            </ul>

            <p>
              <b>3. Coaching Institutes & Tuition Ecosystem</b>
            </p>
            <p>
              Some coaching centers indirectly guide career decisions through
              academic specialization and exam preparation.
            </p>

            <p>
              <b>Loopholes:</b>
            </p>
            <ul>
              <li>Focused on exams, not holistic career understanding</li>
              <li>Push students toward limited, popular career paths</li>
              <li>Starts too late (mostly after Class 9 or 10)</li>
              <li>Does not build self-awareness or decision-making ability</li>
            </ul>

            <p>
              <b>The Core Gap Across All Solutions</b>
            </p>

            <p>None of these solutions are:</p>
            <ul>
              <li>Continuous</li>
              <li>Integrated into the school system</li>
              <li>Focused on early-stage exposure (Class 6 onwards)</li>
              <li>
                Designed to build decision-making ability, not just give advice.
              </li>
            </ul>

            <p>
              That is the reason there is no by default solution for this
              problem.
            </p>
          </AccordionDetails>
        </Accordion>

        {/* Our Solution */}
        <Accordion className="custom-accordion">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Our Solution (Class 6–10)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <p>
              At YAPASSIO,{" "}
              <b>
                we integrate into it through vocational as per NEP and CBSE. We
                solve the pain point of the school.
              </b>
            </p>
            <p>Our model becomes a part of the school curriculum itself.</p>
            <ul>
              <li>No extra burden on teachers</li>
              <li>No additional infrastructure required</li>
              <li>No “extra class” feeling for students</li>
              <li>Designed to be engaging, practical, and curiosity-driven</li>
            </ul>
            <p>
              From Class 6 to 10, students are continuously exposed to different
              career paths, skills, and real-world applications, not
              theoretically, but through structured exploration within the
              school curriculum itself.
            </p>
            <p>
              By the time they reach Class 10, students don’t face confusion,
              they have already{" "}
              <b>experienced and understood different streams in depth.</b>
            </p>
            <p>
              After this journey, students can make their final stream decision
              using our <b>AI platform</b>, which builds on their{" "}
              <b>actual learning exposure throughout school</b>, not just
              one-time tests or assumptions.
            </p>
          </AccordionDetails>
        </Accordion>

        {/* AI + Outcome */}
        <Accordion className="custom-accordion">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">In One Line</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <p>
              <b>
                We integrate career discovery into school, and enable informed
                decisions after Class 10 through real exposure + AI.
              </b>
            </p>
          </AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
};

export default HomePageWriteUpOne;
